import { Form, Formik } from "formik";
import { Check, Copy, KeyRound, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/common/page-header";
import { brand } from "@/config/brand";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { TabShell } from "@/features/settings/components/tab-shell";
import { type ApiKey, createApiKey, listApiKeys, revokeApiKey } from "@/features/settings/firm-api";
import { ApiError } from "@/lib/api";

const schema = Yup.object({
  name: Yup.string().trim().required("Give the key a name").max(255),
  read: Yup.boolean(),
  write: Yup.boolean(),
});

interface CreateValues {
  name: string;
  read: boolean;
  write: boolean;
}

function RawKeyReveal({ rawKey, onDone }: { rawKey: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    toast.success("Copied to clipboard");
  }
  return (
    <Dialog open onOpenChange={(open) => !open && onDone()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy your new API key</DialogTitle>
          <DialogDescription>
            This is the only time the full key is shown. Store it somewhere safe — you
            can&apos;t retrieve it again.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted flex items-center gap-2 rounded-md border p-3">
          <code className="flex-1 font-mono text-sm break-all">{rawKey}</code>
          <Button type="button" size="sm" variant="outline" onClick={copy} className="gap-1.5">
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onDone}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateDialog({ onCreated }: { onCreated: (rawKey: string) => void }) {
  const [open, setOpen] = useState(false);

  async function handleSubmit(values: CreateValues) {
    const scopes = [values.read && "read", values.write && "write"].filter(Boolean) as string[];
    try {
      const { rawKey } = await createApiKey(values.name.trim(), scopes);
      setOpen(false);
      onCreated(rawKey);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't create the key.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button type="button" size="sm" className="gap-1.5" onClick={() => setOpen(true)}>
        <KeyRound className="size-4" />
        Generate Key
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate API key</DialogTitle>
          <DialogDescription>Create a key for integrations like Zapier.</DialogDescription>
        </DialogHeader>
        <Formik<CreateValues>
          initialValues={{ name: "", read: true, write: false }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Key Name" placeholder="e.g. Zapier integration" />
              <div className="grid gap-2">
                <p className="text-sm font-medium">Scopes</p>
                <CheckboxField name="read" label="Read" hint="View data via the API." />
                <CheckboxField name="write" label="Write" hint="Create and update data via the API." />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Generating..." : "Generate"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export function ApiTab() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [error, setError] = useState(false);
  const [rawKey, setRawKey] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listApiKeys()
      .then((k) => active && setKeys(k))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  async function refresh() {
    setKeys(await listApiKeys());
  }

  async function handleRevoke(id: number) {
    try {
      await revokeApiKey(id);
      setKeys((prev) => prev?.filter((k) => k.id !== id) ?? null);
      toast.success("API key revoked");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't revoke the key.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="API"
        subtitle={`Generate keys to connect ${brand.name} to other tools.`}
        actions={keys ? <GenerateDialog onCreated={(k) => refresh().then(() => setRawKey(k))} /> : undefined}
      />
      <TabShell loading={!keys && !error} error={error}>
        {keys && (
        <>
          {keys.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              You don&apos;t have any API keys yet. Generate one to get started.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Scopes</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="font-mono text-xs">{k.maskedKey}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {k.scopes.length ? k.scopes.join(", ") : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "Never"}
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive gap-1.5"
                            >
                              <Trash2 className="size-4" />
                              Revoke
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Any integration using &quot;{k.name}&quot; will immediately stop
                                working. This can&apos;t be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRevoke(k.id)}
                                className="bg-destructive text-white hover:bg-destructive/90"
                              >
                                Revoke
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {rawKey && <RawKeyReveal rawKey={rawKey} onDone={() => setRawKey(null)} />}
        </>
      )}
      </TabShell>
    </div>
  );
}
